<template lang="pug">
.smart-assist-tile(:class="{ 'is-highlight': highlight, 'is-loading': loading }")
  .tile-head
    span.tile-title {{ title }}
    aiq-tooltip(v-if="helpText" :content="helpText" placement="top")
      font-awesome-icon.tile-info(:icon="['fas', 'circle-question']")
  .tile-value(v-if="!loading") {{ formattedValue }}
  .tile-value.tile-value--loading(v-else)
    font-awesome-icon(:icon="['fas', 'spinner']" spin)
  .tile-sub(v-if="subText && !loading" :class="`tile-sub--${subTone}`") {{ subText }}
</template>

<script>
import { isNil } from 'lodash';

const PERCENT_DECIMAL_PLACES = 1;

// Formats a tile's raw value for display. `localized` toggles thousands
// separators (true for on-screen tiles, false for CSV cells where commas
// would corrupt the column).
export function formatTileValue(category, value, { localized = true } = {}) {
  if (isNil(value)) {
    return 'N/A';
  }
  if (category === 'percent') {
    return `${parseFloat(Number(value).toFixed(PERCENT_DECIMAL_PLACES))}%`;
  }
  if (category === 'ratio') {
    return Number(value).toFixed(PERCENT_DECIMAL_PLACES);
  }
  return localized ? Number(value).toLocaleString() : Number(value);
}

export default {
  name: 'SmartAssistTile',
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Number,
      default: null,
    },
    // 'count' | 'percent' | 'ratio'
    category: {
      type: String,
      default: 'count',
    },
    helpText: {
      type: String,
      default: '',
    },
    // Renders the headline-KPI treatment (tinted card, accent value colour).
    highlight: {
      type: Boolean,
      default: false,
    },
    // Optional secondary line under the value (e.g. '4.1 queries / session').
    subText: {
      type: String,
      default: '',
    },
    // Colours the sub line: 'positive' | 'negative' | 'neutral'.
    subTone: {
      type: String,
      default: 'neutral',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    formattedValue() {
      return formatTileValue(this.category, this.value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.smart-assist-tile {
  background: $aiq-bgc-list-item-inactive;
  border: 1px solid transparent;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  min-height: 116px;

  &.is-highlight {
    background: #eceefb;
    border-color: #d4d9f4;
  }

  &.is-loading {
    opacity: 0.7;
  }
}

.tile-head {
  display: flex;
  align-items: center;
  gap: 5px;
}

.tile-title {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.2;
}

.tile-info {
  font-size: 12px;
  color: #9ca3af;
  cursor: help;
}

.tile-value {
  margin-top: 10px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  color: $aiq-text-active;

  .is-highlight & {
    color: #3b46ad;
  }

  &--loading {
    font-size: 22px;
    color: #9ca3af;
  }
}

.tile-sub {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;

  &--positive {
    color: $aiq-color-success;
  }

  &--negative {
    color: $aiq-color-error;
  }

  &--neutral {
    color: #9ca3af;
  }
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .smart-assist-tile {
  background: $aiq-bgc-sidebar-item-hover;
  border-color: $aiq-bdc-input-default;

  &.is-highlight {
    background: #2c3550;
  }

  .tile-value {
    color: $aiq-color-black;
  }
}
</style>
